import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
} from '@mui/material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

import Iconify from '../../../components/iconify';
import { bulkUploadAssets } from '../../../services/assetService';

const ACCEPT =
  '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Client-side guard mirroring the backend's "Only Excel files are allowed".
const isExcelFile = (file) => /\.(xls|xlsx)$/i.test(file?.name || '');

// One example row so users see the expected shape. Column keys are the header
// row — camelCase to match the backend importer's field keys
// ("name", "serialNumber", "purchaseDate", …).
const TEMPLATE_ROWS = [
  {
    name: 'Dell Latitude 5440',
    type: 'Laptop',
    status: 'available',
    condition: 'new',
    serialNumber: 'SN-123456',
    assetTag: 'AT-0001',
    category: 'Hardware',
    location: 'HQ - Floor 2',
    vendor: 'Dell',
    invoiceNumber: 'INV-2024-001',
    purchaseDate: '01/01/2024',
    warrantyExpiry: '01/01/2027',
    purchasePrice: 85000,
    assignedTo: '',
    assignmentNotes: '',
  },
];

export default function AssetBulkUploadModal({ open, onClose, onSuccess }) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(TEMPLATE_ROWS);

    // Format the date columns (K = purchaseDate, L = warrantyExpiry) as real dates.
    TEMPLATE_ROWS.forEach((row, index) => {
      const rowIndex = index + 2; // data starts on the second row (after the header)
      const toDate = (value) => {
        const [d, m, y] = value.split('/');
        return new Date(y, m - 1, d);
      };
      if (row.purchaseDate) ws[`K${rowIndex}`] = { t: 'd', v: toDate(row.purchaseDate), z: 'dd/mm/yyyy' };
      if (row.warrantyExpiry) ws[`L${rowIndex}`] = { t: 'd', v: toDate(row.warrantyExpiry), z: 'dd/mm/yyyy' };
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Assets');
    XLSX.writeFile(wb, 'add_asset_template.xlsx');
  };

  const handlePickFile = () => {
    setErrors([]);
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!isExcelFile(file)) {
      toast.error('Only Excel files are allowed');
      event.target.value = '';
      return;
    }

    setLoading(true);
    setErrors([]);
    try {
      const response = await bulkUploadAssets(file);
      const created = response?.data?.data?.created;
      toast.success(response?.data?.message || `${created ?? ''} assets uploaded successfully`.trim());
      onSuccess?.();
      onClose();
    } catch (error) {
      const rowErrors = error?.response?.data?.errors;
      if (Array.isArray(rowErrors) && rowErrors.length) {
        // Show the validation list so the user can fix the sheet and re-upload.
        setErrors(rowErrors.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))));
      } else {
        toast.error(error?.response?.data?.message || 'Error while uploading file');
      }
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import / Bulk Upload Assets</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          1. Download the template and fill in one asset per row.
          <br />
          2. Save the file as Excel (.xls or .xlsx).
          <br />
          3. Upload the completed file below.
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon="solar:import-bold" />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Iconify icon="solar:export-bold" />}
            onClick={handlePickFile}
            disabled={loading}
          >
            {loading ? 'Uploading…' : 'Upload File'}
          </Button>
          <input
            type="file"
            accept={ACCEPT}
            onChange={handleFileUpload}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
        </Stack>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <AlertTitle>Upload failed — please fix these rows and re-upload</AlertTitle>
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              <List dense disablePadding>
                {errors.map((message, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ListItem key={index} disableGutters>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={message} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AssetBulkUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
